/**
 * Volume File API service.
 * Provides file operations for volumes (list, upload, download, delete, mkdir).
 * Supports both user-scoped and group-scoped volumes.
 *
 * API Endpoints (user-scoped):
 * - GET    /volumes/{name}/files?path=          - List directory contents
 * - GET    /volumes/{name}/files/download?path=  - Download a file
 * - PUT    /volumes/{name}/files/upload?path=    - Upload a file
 * - DELETE /volumes/{name}/files?path=&recursive= - Delete a file or directory
 * - POST   /volumes/{name}/files/mkdir?path=     - Create a directory
 *
 * API Endpoints (group-scoped):
 * - GET    /sandboxGroups/{groupId}/volumes/{name}/files?path=          - List directory contents
 * - GET    /sandboxGroups/{groupId}/volumes/{name}/files/download?path=  - Download a file
 * - PUT    /sandboxGroups/{groupId}/volumes/{name}/files/upload?path=    - Upload a file
 * - DELETE /sandboxGroups/{groupId}/volumes/{name}/files?path=&recursive= - Delete a file or directory
 * - POST   /sandboxGroups/{groupId}/volumes/{name}/files/mkdir?path=     - Create a directory
 *
 * @module services/volumeFileService
 */

import { httpClient } from './httpClient';
import type { VolumeListDirectoryResponse, VolumePathItem } from '@/types';

/**
 * Returns the base URL for volume endpoints, scoped to a group if groupId is provided.
 */
function volumeBase(volumeName: string, groupId?: string): string {
  const encodedName = encodeURIComponent(volumeName);
  if (groupId) {
    const encodedGroupId = encodeURIComponent(groupId);
    return `/sandboxGroups/${encodedGroupId}/volumes/${encodedName}`;
  }
  return `/volumes/${encodedName}`;
}

/**
 * Volume File Service
 * Encapsulates all volume file API operations
 */
export const volumeFileService = {
  /**
   * List directory contents within a volume.
   */
  async listDirectory(
    volumeName: string,
    path: string = '',
    groupId?: string
  ): Promise<VolumeListDirectoryResponse> {
    const response = await httpClient.get<VolumeListDirectoryResponse>(
      `${volumeBase(volumeName, groupId)}/files`,
      { params: { path } }
    );
    return response.data;
  },

  /**
   * Download a file from a volume. Returns a Blob.
   */
  async downloadFile(
    volumeName: string,
    path: string,
    groupId?: string
  ): Promise<{ blob: Blob; fileName: string }> {
    const response = await httpClient.get(`${volumeBase(volumeName, groupId)}/files/download`, {
      params: { path },
      responseType: 'blob',
    });
    const fileName = path.split('/').pop() || 'download';
    return { blob: response.data as Blob, fileName };
  },

  /**
   * Upload a file to a volume.
   */
  async uploadFile(
    volumeName: string,
    path: string,
    file: File,
    overwrite: boolean = false,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void,
    signal?: AbortSignal,
    groupId?: string
  ): Promise<VolumePathItem> {
    const response = await httpClient.put<VolumePathItem>(
      `${volumeBase(volumeName, groupId)}/files/upload`,
      file,
      {
        params: { path, overwrite },
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        onUploadProgress,
        signal,
      }
    );
    return response.data;
  },

  /**
   * Delete a file or directory from a volume.
   */
  async deletePath(
    volumeName: string,
    path: string,
    recursive: boolean = false,
    groupId?: string
  ): Promise<void> {
    await httpClient.delete(`${volumeBase(volumeName, groupId)}/files`, {
      params: { path, recursive },
    });
  },

  /**
   * Create a directory within a volume.
   */
  async createDirectory(
    volumeName: string,
    path: string,
    groupId?: string
  ): Promise<VolumePathItem> {
    const response = await httpClient.post<VolumePathItem>(
      `${volumeBase(volumeName, groupId)}/files/mkdir`,
      null,
      { params: { path } }
    );
    return response.data;
  },
};
